import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import JSZip from 'jszip';
import { errorToast, successToast } from '../../../Toast';
import apiClient from './apiClient';
import loadAssetsClient from './loadAssetsClient';

const ssoApiClient = axios.create({
	baseURL: '/loadassets',
});

const DATE_COMPLIANCE_BULK_PREFIX = '/configuration/products';

export enum ResourceType {
	TABLES = 'tables',
	STATIC_DOCUMENTS = 'staticDocuments',
	DOCUMENT_TEMPLATES = 'documentTemplates',
	FORMULA_TEMPLATES = 'formulaTemplates',
}

export interface ResourceDeployResponse {
	locator: string;
	name: string;
	type: ResourceType;
	effectiveTimestamp: number;
	url: string;
	urlExpirationTimestamp: string;
	success: boolean;
	error: string;
}

export interface DeployResourceRequest {
	effectiveTimestamp: number;
	productName: string;
	resourcesType?: ResourceType;
	file: File;
}

export interface ResourceResponse {
	locator: string;
	name: string;
	effectiveTimestamp: number;
	url: string;
}

export interface ListResourcesResponse {
	resources: ResourceResponse[];
	resourceType: ResourceType;
}

export interface GetResourcesRequest {
	asOfTimestamp: number;
	resourceType?: ResourceType;
	productName: string;
	timestampMatchMode?: 'NEAREST' | 'EXACT';
}

export interface DeleteResourceRequest {
	locator: string;
	resourceType: ResourceType;
	productName: string;
}

const getResourcesTimestamps = async (
	productName: string,
	authToken: string,
): Promise<number[]> => {
	let url = `/products/resources/timestamps?productName=${productName}`;

	const res = await apiClient.get<string[]>(url, {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data.map((ts) => parseInt(ts));
};

const getResourcesTimestampsSSO = async (
	productName: string,
	tenantHostname: string,
) => {
	let url = `/products/resources/timestamps?productName=${productName}&ns_tenant_host=${tenantHostname}`;
	const res = await apiClient.get<string[]>(url);

	return res.data.map((ts) => parseInt(ts));
};

const getResources = async (
	request: GetResourcesRequest,
	authToken: string,
) => {
	let url = `${DATE_COMPLIANCE_BULK_PREFIX}/${request.productName}/resources/${request.resourceType}`;

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.timestampMatchMode) {
		params.append('timestampMatchMode', request.timestampMatchMode);
	}

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await loadAssetsClient.get<ResourceResponse[]>(url, {
		headers: {
			Authorization: authToken,
		},
	});

	return res.data;
};

const getResourcesSSO = async (
	request: GetResourcesRequest,
	tenantHostname: string,
) => {
	let url = `${DATE_COMPLIANCE_BULK_PREFIX}/${request.productName}/resources/${request.resourceType}`;

	const params = new URLSearchParams();

	if (request.asOfTimestamp) {
		params.append('asOfTimestamp', request.asOfTimestamp.toString());
	}
	if (request.timestampMatchMode) {
		params.append('timestampMatchMode', request.timestampMatchMode);
	}

	params.append('ns_tenant_host', tenantHostname);

	if (Array.from(params).length > 0) {
		url += `?${params.toString()}`;
	}

	const res = await ssoApiClient.get<ResourceResponse[]>(url);

	return res.data;
};

async function buildFormData(request: DeployResourceRequest) {
	const formData = new FormData();
	let url = `${DATE_COMPLIANCE_BULK_PREFIX}/${request.productName}/resources`;
	if (request.resourcesType) {
		url += `/${request.resourcesType}`;
	}
	if (request.file.type.includes('zip')) {
		formData.append('file', request.file);
	} else {
		//handle zip conversion
		const zip = new JSZip();
		const fileContent = await request.file.arrayBuffer();
		zip.file(request.file.name, fileContent);
		const zipContent = await zip.generateAsync({ type: 'blob' });
		formData.append('file', zipContent, 'resources.zip');
	}

	if (request.effectiveTimestamp) {
		formData.append(
			'effectiveTimestamp',
			request.effectiveTimestamp.toString(),
		);
	}
	return { formData, url };
}

const deployResource = async (
	request: DeployResourceRequest,
	authToken: string,
) => {
	let { formData, url } = await buildFormData(request);

	const response = await loadAssetsClient.put<ResourceDeployResponse[]>(
		url,
		formData,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
	return response.data;
};

const deployResourceSSO = async (
	request: DeployResourceRequest,
	tenantHostname: string,
) => {
	let { formData, url } = await buildFormData(request);
	url = `${url}/?ns_tenant_host=${tenantHostname}`;
	const response = await ssoApiClient.put<ResourceDeployResponse[]>(
		url,
		formData,
	);
	return response.data;
};

const deleteResource = async (
	request: DeleteResourceRequest,
	authToken: string,
) => {
	const url = `${DATE_COMPLIANCE_BULK_PREFIX}/${request.productName}/resources/${request.resourceType}/${request.locator}`;
	const response = await loadAssetsClient.delete<any>(url, {
		headers: {
			Authorization: authToken,
		},
	});
	return response.data;
};

const deleteResourceSSO = async (
	request: DeleteResourceRequest,
	tenantHostname: string,
) => {
	const url = `${DATE_COMPLIANCE_BULK_PREFIX}/${request.productName}/resources/${request.resourceType}/${request.locator}?ns_tenant_host=${tenantHostname}`;
	const response = await ssoApiClient.delete<any>(url);
	return response.data;
};

const doGetResources = async (
	tenantHostname: string,
	request: GetResourcesRequest,
	token: string,
): Promise<ResourceResponse[]> => {
	return tenantHostname
		? await getResourcesSSO(request, tenantHostname)
		: await getResources(request, token);
};

const getFirstNonEmptyResources = async (
	request: GetResourcesRequest,
	token: string,
	resourceTypes: ResourceType[],
	tenantHostname?: string,
): Promise<ListResourcesResponse> => {
	if (resourceTypes.length === 0) {
		return {
			resourceType: ResourceType.TABLES, //fallback to tables if not nothing was found
			resources: [],
		};
	}

	const resourceType = resourceTypes[0];
	request.resourceType = resourceType;
	const resources = await doGetResources(tenantHostname, request, token);

	if (resources.length > 0) {
		return {
			resourceType,
			resources,
		};
	}
	// Recursively call the function with the remaining resource types
	return getFirstNonEmptyResources(
		request,
		token,
		resourceTypes.slice(1),
		tenantHostname,
	);
};

const useResources = (
	queryKey: string[],
	request: GetResourcesRequest,
	token: string,
	tenantHostname?: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			try {
				if (request.resourceType) {
					return {
						resourceType: request.resourceType,
						resources: await doGetResources(tenantHostname, request, token),
					};
				}

				const resourceTypesToCheck = [
					ResourceType.TABLES,
					ResourceType.STATIC_DOCUMENTS,
					ResourceType.DOCUMENT_TEMPLATES,
					ResourceType.FORMULA_TEMPLATES,
				];

				return await getFirstNonEmptyResources(
					request,
					token,
					resourceTypesToCheck,
					tenantHostname,
				);
			} catch (err) {
				if (axios.isAxiosError(err) && err?.response?.data?.error) {
					return Promise.reject(err.response.data.error);
				}
				return Promise.reject('Unknown error.');
			}
		},
	});
};

// Mutations
const useAddResourceMutation = (
	queryKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(newResource: DeployResourceRequest) => {
			return tenantHostname
				? deployResourceSSO(newResource, tenantHostname)
				: deployResource(newResource, token);
		},
		{
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.error) {
					errorToast('Error uploading resources: ' + err.response.data.error);
				} else {
					errorToast('Error uploading resources');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useDeleteResourceMutation = (
	queryKey: string[],
	resourcesDatesKey: string[],
	token: string,
	tenantHostname?: string,
) => {
	const client = useQueryClient();
	return useMutation(
		(request: DeleteResourceRequest) => {
			return tenantHostname
				? deleteResourceSSO(request, tenantHostname)
				: deleteResource(request, token);
		},
		{
			onSuccess: () => {
				successToast('Resource deleted');
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast('Error deleting resource: ' + err.response.data.message);
				} else {
					errorToast('Error deleting resource');
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
				client.invalidateQueries(resourcesDatesKey);
			},
		},
	);
};

const useResourcesTimestamps = (
	queryKey: string[],
	productName: string,
	token: string,
	tenantHostname?: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			return tenantHostname
				? await getResourcesTimestampsSSO(productName, tenantHostname)
				: await getResourcesTimestamps(productName, token);
		},
	});
};

const ResourcesAPI = {
	useAddResourceMutation,
	useDeleteResourceMutation,
	useResources,
	useResourcesTimestamps,
	getResourcesTimestamps,
};

export default ResourcesAPI;
